import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  X, Radio, Timer, Trophy, MapPin, User, Car, Flag, Zap, Activity, Pause,
} from 'lucide-react';
import {
  LapDeltaBadge, TrackPositionBar, IncidentPill,
  isShiftLight, isCaution, RigStatusBadge, isSessionInitialising, GridInitialisingPlaceholder,
} from '@/components/telemetry/TelemetryVisuals';
import ShiftLights from '@/components/telemetry/ShiftLights';
import PedalBar from '@/components/telemetry/PedalBar';
import FpsBadge from '@/components/telemetry/FpsBadge';
import { prettifyAssettoName, prettifyDriverName, isAcFamily } from '@/lib/telemetryPrettify';
import { useSpeedUnit, formatSpeed, speedUnitLabel } from '@/hooks/useSpeedUnit';

const SIM_BADGE = {
  iracing: { label: 'iRACING', cls: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30' },
  assetto_corsa: { label: 'ASSETTO CORSA', cls: 'bg-amber-500/15 text-amber-400 border-amber-500/30' },
  assetto_corsa_competizione: { label: 'ACC', cls: 'bg-sky-500/15 text-sky-400 border-sky-500/30' },
  none: { label: 'IDLE', cls: 'bg-white/5 text-white/40 border-white/10' },
};

function fmtGear(g) {
  if (g === null || g === undefined) return '–';
  if (g < 0) return 'R';
  if (g === 0) return 'N';
  return String(g);
}

/**
 * Venue rig detail popup.
 *
 * Opens centred over the live board and is fed the rig the board is already
 * streaming, so it keeps updating while it is open. Deliberately carries no
 * links at all: a wall display must never be able to navigate away from itself.
 */
export default function RigDetailModal({ rig, onClose }) {
  const speedUnit = useSpeedUnit();

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  if (!rig) return null;

  const last = rig.last_update ? new Date(rig.last_update).getTime() : 0;
  const live = last && (Date.now() - last < 6000);
  const standby = !last || (Date.now() - last >= 60000);
  const simBadge = SIM_BADGE[rig.sim] || SIM_BADGE.none;
  const shiftFlash = isShiftLight(rig);
  const showCaution = isCaution(rig.session_flags);
  const showPos = live && rig.position != null && rig.position > 0;
  const initialising = isSessionInitialising(rig);
  const isPaused = rig.is_paused === true;
  const rpmPct = rig.max_rpm && rig.rpm ? Math.min(100, (rig.rpm / rig.max_rpm) * 100) : null;

  // A paused sim keeps reporting the last inputs, which would leave the pedals
  // and RPM frozen at whatever they were — zero them so the popup reads as
  // stopped rather than stuck.
  const displayThrottle = isPaused ? 0 : rig.throttle;
  const displayBrake = isPaused ? 0 : rig.brake;
  const displayRpm = isPaused ? null : rig.rpm;
  const displayRpmPct = isPaused ? null : rpmPct;

  const displayDriver = prettifyDriverName(rig.driver_name, rig.rig_key) || 'Guest Driver';
  const displayCar = isAcFamily(rig.sim) ? prettifyAssettoName(rig.car) : rig.car;
  const displayTrack = isAcFamily(rig.sim) ? prettifyAssettoName(rig.track) : rig.track;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.15 }}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={`${rig.rig_name || rig.rig_key} live detail`}
      className="fixed inset-0 z-[100] flex items-center justify-center p-3 sm:p-6 lg:p-10 bg-black/75 backdrop-blur-sm"
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.97 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.18, ease: 'easeOut' }}
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-4xl max-h-full overflow-y-auto bg-[#0f0f13] border border-white/10 shadow-2xl"
      >
        {/* header */}
        <div className="sticky top-0 z-10 bg-[#0f0f13]/95 backdrop-blur border-b border-white/10 px-5 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <Radio className={`w-5 h-5 shrink-0 ${live ? 'text-primary' : 'text-white/30'}`} />
            <div className="min-w-0">
              <p className="text-white font-black text-lg sm:text-xl uppercase tracking-tight truncate">
                {rig.rig_name || rig.rig_key}
              </p>
              <p className="text-white/40 text-[10px] font-mono font-bold tracking-widest uppercase">{rig.rig_key}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {rig.sim && rig.sim !== 'none' && (
              <span className={`hidden sm:inline-block text-[9px] font-bold tracking-widest uppercase px-2 py-1 border ${simBadge.cls}`}>
                {simBadge.label}
              </span>
            )}
            <RigStatusBadge rig={rig} size="lg" variant="pill" />
            <button
              type="button"
              onClick={onClose}
              aria-label="Close rig detail"
              className="w-11 h-11 flex items-center justify-center border border-white/15 text-white/60 hover:text-white hover:border-white/40 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* caution */}
        {showCaution && (
          <div className="flex items-center gap-3 border-b border-amber-400/40 bg-amber-400/10 px-5 py-3 animate-caution-pulse">
            <Flag className="w-4 h-4 text-amber-400 shrink-0" />
            <span className="text-amber-400 font-bold tracking-widest uppercase text-xs">Caution — yellow flag active</span>
          </div>
        )}

        <div className="p-5">
          {standby ? (
            <div className="flex flex-col items-center justify-center gap-2 py-16">
              <Radio className="w-7 h-7 text-white/15" />
              <p className="text-white/30 text-xs font-bold tracking-widest uppercase">Rig on Standby</p>
              <p className="text-white/20 text-[10px]">The rig reports here as soon as the pusher connects.</p>
            </div>
          ) : initialising ? (
            <GridInitialisingPlaceholder size="lg" />
          ) : (
            <div className="space-y-4">
              {/* identity */}
              <div className="flex flex-wrap items-center gap-2">
                <Badge icon={<User className="w-3 h-3" />} label={displayDriver || '—'} />
                <Badge icon={<Car className="w-3 h-3" />} label={displayCar || '—'} />
                <Badge icon={<MapPin className="w-3 h-3" />} label={displayTrack || '—'} />
                {rig.session_type && <Badge icon={<Flag className="w-3 h-3" />} label={rig.session_type} />}
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
                {/* cockpit */}
                <div className={`lg:col-span-3 bg-[#121214] border p-5 ${shiftFlash ? 'animate-shift-flash' : 'border-white/10'}`}>
                  <div className="flex items-center justify-between mb-4">
                    <span className="flex items-center gap-2 text-white/40 text-[10px] font-bold tracking-widest uppercase">
                      <Activity className="w-3.5 h-3.5" /> Cockpit
                    </span>
                    <FpsBadge fps={rig.fps} />
                  </div>

                  {isPaused && (
                    <div className="flex items-center justify-center gap-2 border border-slate-400/30 bg-slate-400/5 px-4 py-2 mb-4">
                      <Pause className="w-3.5 h-3.5 text-slate-400" />
                      <span className="text-slate-400 font-bold tracking-widest uppercase text-[10px]">Session Paused</span>
                    </div>
                  )}

                  <div className={`flex items-stretch gap-4 mb-5 ${isPaused ? 'opacity-60' : ''}`}>
                    <div className="flex items-stretch justify-center gap-4 bg-[#0a0a0c] border border-white/10 p-4 w-full max-w-[190px]">
                      <PedalBar label="Brake" value={displayBrake} color="#dd1f26" glowRgba="rgba(221,31,38,0.5)" />
                      <PedalBar label="Throttle" value={displayThrottle} color="#10b981" glowRgba="rgba(16,185,129,0.4)" />
                    </div>
                    <div className="relative flex-1 flex flex-col items-center justify-center bg-[#0a0a0c] border border-white/10 p-5 overflow-hidden">
                      <div className="absolute top-0 left-0 right-0 h-1 bg-[#dd1f26]" />
                      <p className="text-white/30 text-[10px] font-bold tracking-widest uppercase mb-1">Gear</p>
                      <span className={`font-black text-6xl font-mono leading-none ${rig.gear && rig.gear > 0 ? 'text-[#dd1f26]' : 'text-white/50'}`}>
                        {fmtGear(rig.gear)}
                      </span>
                      <p className="text-white/30 text-[10px] font-bold tracking-widest uppercase mt-4 mb-1">Speed</p>
                      <div className="flex items-baseline gap-2">
                        <span className="text-white font-black text-4xl tabular-nums leading-none font-mono">
                          {formatSpeed(rig.speed_kph, speedUnit)}
                        </span>
                        <span className="text-white/40 text-xs font-bold">{speedUnitLabel(speedUnit)}</span>
                      </div>
                    </div>
                  </div>

                  <div className={`bg-[#0a0a0c] border border-white/10 p-4 ${shiftFlash ? 'animate-shift-flash' : ''}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-white/30 text-[10px] font-bold tracking-widest uppercase">RPM</span>
                      <span className="text-white/50 text-xs font-mono tabular-nums">
                        {displayRpm != null ? Math.round(displayRpm).toLocaleString() : '–'}
                        <span className="text-white/30"> / {rig.max_rpm ? Math.round(rig.max_rpm).toLocaleString() : '—'}</span>
                      </span>
                    </div>
                    <ShiftLights rpmPct={displayRpmPct} shiftFlash={shiftFlash} />
                  </div>
                </div>

                {/* timing & track */}
                <div className="lg:col-span-2 bg-[#121214] border border-white/10 p-5 flex flex-col">
                  <span className="flex items-center gap-2 text-white/40 text-[10px] font-bold tracking-widest uppercase mb-4">
                    <Timer className="w-3.5 h-3.5" /> Timing &amp; Track
                  </span>

                  <div className="grid grid-cols-2 gap-3">
                    <Tile label="Last Lap" icon={<Timer className="w-3 h-3 text-white/40" />} extra={<LapDeltaBadge delta={rig.lap_delta} variant="inline" />}>
                      <span className={`font-mono font-bold text-lg tabular-nums ${rig.lap_time ? 'text-white' : 'text-white/25'}`}>
                        {rig.lap_time || '--:--.---'}
                      </span>
                    </Tile>
                    <Tile label="Best Lap" icon={<Trophy className="w-3 h-3 text-primary" />}>
                      <span className={`font-mono font-bold text-lg tabular-nums ${rig.best_lap_time ? 'text-primary' : 'text-white/25'}`}>
                        {rig.best_lap_time || '--:--.---'}
                      </span>
                    </Tile>
                    <Tile label="Lap" icon={<Flag className="w-3 h-3 text-white/40" />}>
                      <span className="text-white font-mono font-bold text-lg tabular-nums">{rig.lap != null ? rig.lap : '–'}</span>
                    </Tile>
                    <Tile label="Position" icon={<Zap className="w-3 h-3 text-white/40" />}>
                      {showPos
                        ? <span className="font-mono font-black text-lg text-white">P{rig.position}</span>
                        : <span className="text-white/40 font-mono font-bold text-lg">–</span>}
                    </Tile>
                  </div>

                  {rig.incidents != null && (
                    <div className="mt-4"><IncidentPill incidents={rig.incidents} size="lg" /></div>
                  )}

                  <div className="mt-auto pt-5">
                    <div className="flex items-center justify-between mb-2">
                      <span className="flex items-center gap-1.5 text-white/30 text-[9px] font-bold tracking-widest uppercase">
                        <MapPin className="w-3 h-3" /> Track Position
                      </span>
                      {rig.lap_dist_pct != null && (
                        <span className="text-white/50 text-xs font-mono tabular-nums">
                          {`${Math.round(Number(rig.lap_dist_pct) * 100)}%`}
                        </span>
                      )}
                    </div>
                    {rig.lap_dist_pct != null ? (
                      <TrackPositionBar pct={rig.lap_dist_pct} accent="#dd1f26" size="lg" />
                    ) : (
                      <div className="h-2.5 bg-white/5 flex items-center justify-center">
                        <span className="text-white/15 text-[8px] font-mono tracking-widest uppercase">Awaiting track data</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}

function Badge({ icon, label }) {
  return (
    <span className="inline-flex items-center gap-1.5 border border-white/10 bg-black/30 px-3 py-1.5 text-white/70 text-xs font-medium">
      {icon}
      <span className="truncate max-w-[200px]">{label}</span>
    </span>
  );
}

function Tile({ label, icon, extra, children }) {
  return (
    <div className="bg-[#141416] border border-[#26262a] px-3 py-3">
      <div className="flex items-center justify-between gap-1 mb-1.5">
        <div className="flex items-center gap-1.5">
          {icon}
          <span className="text-white/30 text-[9px] font-bold tracking-widest uppercase">{label}</span>
        </div>
        {extra}
      </div>
      {children}
    </div>
  );
}